import { FC, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { useDispatch } from 'react-redux';
import DefectsListManager from './DefectsListManager';
import { patchTestResultRequest } from '@pages/testruns/store';
import useDefectLinkManager from '../hooks/useDefectLinkManager';
import { Defect } from '../types';
import { editTestCaseRequest } from '@pages/testcases/store';

interface DefectLinkDialogProps {
    open: boolean;
    onClose: () => void;
    entityType: 'testresult' | 'testcase';
    entityId: number;
    defects: Defect[];
}

const DefectLinkDialog: FC<DefectLinkDialogProps> = ({ open, onClose, entityType, entityId, defects }) => {
    const { t } = useTranslation();
    const { defectsArrLocal, defectsToToggleLink, handleLinkDefect, handleUnlinkDefect, resetDefectChanges } =
        useDefectLinkManager(defects);
    const dispatch = useDispatch();
    const [selectedDefectPk, setSelectedDefectPk] = useState<number | null>(null);
    const isTestcase = entityType === 'testcase';
    const isDefectAlreadyAdded = Boolean(selectedDefectPk && defects.some((d) => d.pk === selectedDefectPk));

    const handleSelect = (defect: Defect | null) => {
        setSelectedDefectPk(defect?.pk ?? null);
    };

    const handleSaveDefectsList = () => {
        const ids = isTestcase && selectedDefectPk ? [selectedDefectPk] : defectsToToggleLink;

        if (entityType === 'testresult') {
            dispatch(
                //@ts-ignore
                patchTestResultRequest({
                    testresultId: entityId,
                    data: { defects_ids_to_toggle_link: ids },
                }),
            );
        } else {
            dispatch(
                //@ts-ignore
                editTestCaseRequest({
                    id: entityId,
                    params: { defects_ids_to_toggle_link: ids },
                }),
            );
        }
        onClose();
        resetDefectChanges();
        setSelectedDefectPk(null);
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose();
                resetDefectChanges();
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{t('link_defect')}</DialogTitle>
            <DialogContent sx={{ overflow: 'visible' }}>
                <DefectsListManager
                    defects={defectsArrLocal}
                    onAdd={handleLinkDefect}
                    onRemove={handleUnlinkDefect}
                    showList={!isTestcase}
                    onSelect={handleSelect}
                />
            </DialogContent>
            <DialogActions>
                <UtmsButton
                    onClick={handleSaveDefectsList}
                    disabled={isTestcase ? !selectedDefectPk || isDefectAlreadyAdded : !defectsToToggleLink.length}
                >
                    {t('save')}
                </UtmsButton>
                <UtmsButton color="secondary" onClick={onClose}>
                    {t('cancel')}
                </UtmsButton>
            </DialogActions>
        </Dialog>
    );
};

export default DefectLinkDialog;
